import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper
} from '@mui/material';
import {
    ArrowForward as ArrowForwardIcon,
    ArrowBack as ArrowBackIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
    Lock as LockIcon,
    Warning as WarningIcon,
    HealthAndSafety as HealthAndSafetyIcon,
    OpenInNew as OpenInNewIcon,
    CheckCircle as CheckCircleIcon,
    PictureAsPdf as PictureAsPdfIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { initializeDB } from '../../../config/db';
import { JHA_STORE, STATUS_NEW, STATUS_PROCESS } from '../../../config/constants';
import { ensureAllStepsStructure } from '../utils/stepTwoUtils';
import { normalizeName, byName, sortEnergyList, sortWorkActivities } from './energyOptions';
import JhaStepHeader from '../JhaStepHeader';
import HazardActionsModal from './HazardActionsModal';
import { createJhaSkeleton } from '../utils/jhaRecordFactory';
import { generateUUID } from '../../../utils/uuid';
import Swal from 'sweetalert2';
import { findMissingDocumentsLocal, evaluateFarpRequirement, isFarpAttachedOrLinked } from '../utils/documentTriggerEvaluator';
import fallArrestRescuePlanService from '../../../services/fallArrestRescuePlanService';
import axiosInstance from '../../../axiosInstance';
import { localDateString } from '../../../utils/localDate';

const TODAY_STRING = localDateString();

// Swal по умолчанию ставит .swal2-container с z-index 1060, а MUI AppBar/header
// — 1100, поэтому toast визуально перекрывается шапкой. Поднимаем контейнер
// выше всего: 13000 (> MUI Modal=1300, > Drawer=1200, > AppBar=1100).

function getHazardKey(hazard) {
    if (!hazard) return '';
    return hazard._key || hazard.id || hazard.ID || hazard.value || hazard.name || '';
}

function normalizeRow(row) {
    const baseId = row.id || `row-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    const existingSelections = Array.isArray(row.energySelections)
        ? row.energySelections
        : row.energyId
        ? [
              {
                  id: `sel-${baseId}`,
                  energyKey: row.energyKey || null,
                  energyId: row.energyId,
                  energyName: row.energyName || '',
                  hazards: Array.isArray(row.hazards) ? row.hazards : []
              }
          ]
        : [];

    const energySelections = existingSelections.map((selection, index) => ({
        id: selection.id || `sel-${baseId}-${index}`,
        energyKey: selection.energyKey || null,
        energyId: selection.energyId || null,
        energyName: selection.energyName || '',
        hazards: Array.isArray(selection.hazards) ? selection.hazards : []
    }));

    const primary = energySelections[0] || {};

    return {
        id: baseId,
        workActivity: row.workActivity || '',
        energySelections,
        // legacy fields for compatibility
        energyId: primary.energyId || null,
        energyName: primary.energyName || '',
        hazards: primary.hazards || [],
        actions: row.actions || ''
    };
}

export default function JhaStepThree() {
    const navigate = useNavigate();
    const { state } = useLocation() || {};
    const { jha = null, isNew = false, viewMode = false, fromViewer = false } = state || {};

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [energyList, setEnergyList] = useState([]);
    const [workActivitiesList, setWorkActivitiesList] = useState([]);
    const [jhaData, setJhaData] = useState(null);
    const [hazardModalOpen, setHazardModalOpen] = useState(false);
    const [pendingHazard, setPendingHazard] = useState(null);
    const [pendingSelection, setPendingSelection] = useState(null);
    const [lockLifted, setLockLifted] = useState(false);

    // ─── Fall Arrest Rescue Plan integration state ─────────────────
    // Список trigger-WorkActivities (offline из FARP_TRIGGER_STORE) — legacy,
    // только для отображения "Triggered by: <activity>".
    const [farpTriggerValues, setFarpTriggerValues] = useState([]);
    // Существующий submitted FARP за (jobCode + today). null если нет.
    const [farpExistingPlan, setFarpExistingPlan] = useState(null);
    const [farpCheckLoading, setFarpCheckLoading] = useState(false);
    // FARP required по ПОЛНОМУ оценщику (work-activity / energy / hazard).
    const [farpRequired, setFarpRequired] = useState(false);
    const [farpReason, setFarpReason] = useState('');
    // FARP выполнен фото/привязкой/поданной формой → баннер зелёный, без «required».
    const farpSatisfied = useMemo(
        () => Boolean(farpExistingPlan) || isFarpAttachedOrLinked(jhaData),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [farpExistingPlan, jhaData?.stepTwo]
    );

    // ─── Generic document-triggers state ──────────────────────────
    // Список документов, чьи триггеры сматчили текущий stepThree, но в stepTwo
    // нет ни фото, ни linked permit'а. Пересчитывается live при изменении JHA.
    // FARP отфильтрован отдельно, потому что для него есть свой кастомный баннер.
    const [triggeredMissing, setTriggeredMissing] = useState([]);

    const energyOptionById = useMemo(() => {
        const map = new Map();
        (energyList || []).forEach(option => {
            const key = option.id || option.ID || option.value;
            if (key !== undefined) {
                map.set(key, option);
            }
        });
        return map;
    }, [energyList]);

    const energyOptionByKey = useMemo(() => {
        const map = new Map();
        (energyList || []).forEach(option => {
            if (option._key) {
                map.set(option._key, option);
            }
        });
        return map;
    }, [energyList]);

    // Резолв по имени — страховка для JHA, созданных до схлопывания дублей:
    // там в energySelections может лежать id копии, которой в списке больше нет.
    const energyOptionByName = useMemo(() => {
        const map = new Map();
        (energyList || []).forEach(option => {
            const key = normalizeName(option.value);
            if (key && !map.has(key)) {
                map.set(key, option);
            }
        });
        return map;
    }, [energyList]);

    const resolveEnergyOption = useCallback(selection => {
        if (!selection) return null;
        if (selection.energyKey && energyOptionByKey.has(selection.energyKey)) {
            return energyOptionByKey.get(selection.energyKey);
        }
        if (selection.energyId && energyOptionById.has(selection.energyId)) {
            return energyOptionById.get(selection.energyId);
        }
        return energyOptionByName.get(normalizeName(selection.energyName)) || null;
    }, [energyOptionById, energyOptionByKey, energyOptionByName]);

    const saveTimeoutRef = useRef(null);
    const pendingRecordRef = useRef(null);
    const createdRecordIdRef = useRef(jha?.id || null);

    const isSubmitted = jhaData && (jhaData.status === 'finish' || jhaData.status === 'completed' || jhaData.syncStatus === 'synced');

    // Check if crew has signed on step 4 - if so, editing should be locked
    const crewAcknowledgments = jhaData?.stepFour?.employeeAcknowledgments || [];
    const hasCrewSignatures = crewAcknowledgments.some(ack => ack.signature);
    const isEditingLocked = hasCrewSignatures && !lockLifted && !isSubmitted && !viewMode;

    // isReadOnly includes locked state - all form controls use this
    const isReadOnly = isSubmitted || viewMode || isEditingLocked;

    // ─── FARP integration: derived state ─────────────────────────────
    // Список trigger-activity которые юзер уже выбрал в Step3 → если есть,
    // нужен Fall Arrest Rescue Plan.
    const triggeredActivities = useMemo(() => {
        if (!jhaData?.stepThree?.energyExposures || farpTriggerValues.length === 0) return [];
        const triggerSet = new Set(farpTriggerValues);
        const seen = new Set();
        const result = [];
        for (const row of jhaData.stepThree.energyExposures) {
            const wa = (row.workActivity || '').trim();
            if (wa && triggerSet.has(wa) && !seen.has(wa)) {
                seen.add(wa);
                result.push(wa);
            }
        }
        return result;
    }, [jhaData?.stepThree?.energyExposures, farpTriggerValues]);

    const farpJobCode    = jhaData?.stepOne?.jobNumber || '';
    const farpDate       = jhaData?.stepOne?.date || TODAY_STRING;
    // localId — стабильный UUID конкретного JHA. Используется для точной связи
    // с FARP (один JHA → один FARP), даже если на джобе за день несколько JHA
    // для разных работ.
    const farpJhaLocalId = jhaData?.localId || '';

    // Load FARP trigger values on mount (offline-first из IDB)
    useEffect(() => {
        (async () => {
            const values = await fallArrestRescuePlanService.getTriggerValues();
            setFarpTriggerValues(values);
        })();
    }, []);

    // FARP required — по ПОЛНОМУ оценщику (work-activity / energy / hazard).
    // Раньше баннер заводился только от work-activity-only условий, поэтому для
    // energy/hazard-триггеров (как на проде: Mobile Equipment + Falls from Height)
    // не показывался. Теперь показывается всегда, когда триггер сматчился.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            const { required, reason } = await evaluateFarpRequirement(jhaData);
            if (!cancelled) { setFarpRequired(required); setFarpReason(reason); }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jhaData?.stepThree, jhaData?.stepOne]);

    // When trigger detected — check backend for existing submitted FARP.
    // Точная связь по jhaLocalId если он есть; иначе fallback на job+date.
    useEffect(() => {
        if (!farpRequired) {
            setFarpExistingPlan(null);
            return;
        }
        // Need at least jhaLocalId or (jobCode + date) to check
        if (!farpJhaLocalId && (!farpJobCode || !farpDate)) {
            setFarpExistingPlan(null);
            return;
        }
        let cancelled = false;
        (async () => {
            setFarpCheckLoading(true);
            const criteria = farpJhaLocalId
                ? { jhaLocalId: farpJhaLocalId }
                : { jobCode: farpJobCode, date: farpDate };
            const { exists, plan } = await fallArrestRescuePlanService.checkExisting(criteria);
            if (cancelled) return;
            setFarpExistingPlan(exists ? plan : null);
            setFarpCheckLoading(false);
        })();
        return () => { cancelled = true; };
    }, [farpRequired, farpJhaLocalId, farpJobCode, farpDate]);

    // Live trigger evaluation: пересчитываем missing-docs при каждом изменении
    // jhaData (выбор Work Activity / Energy / Hazard или аттач документа на step2).
    // Включаем все триггеры — FARP по новой системе тоже может срабатывать по
    // Energy+Hazard, и старый FARP-баннер (legacy WA-only) такие случаи не ловит.
    useEffect(() => {
        if (!jhaData) {
            setTriggeredMissing([]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const missing = await findMissingDocumentsLocal(jhaData);
                if (cancelled) return;
                setTriggeredMissing(missing || []);
            } catch (e) {
                console.warn('[JhaStep3] live trigger evaluation failed', e);
            }
        })();
        return () => { cancelled = true; };
    }, [jhaData?.stepThree, jhaData?.stepTwo, jhaData?.localId]);

    const openFarpForm = () => {
        const params = new URLSearchParams();
        if (farpJobCode)    params.set('jobCode', farpJobCode);
        if (farpDate)       params.set('planDate', farpDate);
        // jhaLocalId — точная связь FARP↔JHA. Без него у джоба за день мы не
        // отличили бы FARP для разных работ.
        if (farpJhaLocalId) params.set('jhaLocalId', farpJhaLocalId);
        // returnTo — путь обратно в JHA. После перестановки шагов Activities = step-2.
        // Если submitted JHA — на viewer.
        let jhaPath;
        if (isSubmitted && jhaData?.id) {
            jhaPath = `/jha/view/${jhaData.id}`;
        } else if (jhaData?.id) {
            jhaPath = `/jha/step-2?id=${encodeURIComponent(jhaData.id)}`;
        } else {
            jhaPath = '/jha';
        }
        params.set('returnTo', jhaPath);
        navigate(`/safety/forms/fall-arrest-rescue-plan/form?${params.toString()}`);
    };

    // Открыть PDF существующего submitted FARP в новой вкладке (через axios blob —
    // нужен Bearer token).
    const viewExistingFarpPdf = async () => {
        if (!farpExistingPlan?.id) return;
        try {
            const resp = await axiosInstance.get(`/fall-arrest-rescue-plan/${farpExistingPlan.id}/pdf`, {
                responseType: 'blob',
            });
            const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        } catch (e) {
            Swal.fire('Error', 'Failed to load PDF: ' + (e.message || 'unknown'), 'error');
        }
    };

    useEffect(() => {
        loadData();
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadData = useCallback(async () => {
        setLoading(true);
        const db = await initializeDB();

        const [allEnergy, allWorkActivities] = await Promise.all([
            db.transaction('energy', 'readonly').objectStore('energy').getAll(),
            db.transaction('workActivities', 'readonly').objectStore('workActivities').getAll()
        ]);
        setEnergyList(sortEnergyList(allEnergy || []));
        setWorkActivitiesList(sortWorkActivities(allWorkActivities || []));

        const tx = db.transaction(JHA_STORE, 'readwrite');
        const store = tx.objectStore(JHA_STORE);
        let currentRecord = null;

        if (createdRecordIdRef.current) {
            currentRecord = await store.get(createdRecordIdRef.current);
        }

        if (!currentRecord && jha?.id) {
            currentRecord = await store.get(jha.id);
        }

        if (!currentRecord && isNew) {
            const newId = generateUUID();
            currentRecord = createJhaSkeleton({
                id: newId,
                status: STATUS_NEW,
                createdAt: new Date().toISOString(),
                docs: [],
                evaluation: [],
                today: TODAY_STRING,
                metaLastStep: 3,
            });
            await store.put(currentRecord);
            createdRecordIdRef.current = currentRecord.id;
        }

        if (!currentRecord && jha) {
            const skeleton = createJhaSkeleton({
                id: jha.id,
                status: jha.status || STATUS_NEW,
                createdAt: jha.createdAt || new Date().toISOString(),
                docs: [],
                evaluation: [],
                today: TODAY_STRING,
                metaLastStep: 3,
            });

            currentRecord = {
                ...skeleton,
                ...jha,
                stepOne: { ...skeleton.stepOne, ...(jha.stepOne || {}) },
                stepTwo: { ...skeleton.stepTwo, ...(jha.stepTwo || {}) },
                stepThree: { ...skeleton.stepThree, ...(jha.stepThree || {}) },
                stepFour: { ...skeleton.stepFour, ...(jha.stepFour || {}) },
                meta: { ...skeleton.meta, ...(jha.meta || {}) },
            };
            await store.put(currentRecord);
            createdRecordIdRef.current = currentRecord.id;
        }

        if (!currentRecord) {
            setLoading(false);
            navigate('/jha');
            return;
        }

        const structured = ensureAllStepsStructure({ ...currentRecord }, null, null, '', TODAY_STRING);

        if (structured.meta?.lastStep === undefined || structured.meta.lastStep < 3) {
            structured.meta = { ...(structured.meta || {}), lastStep: 3 };
            await store.put(structured);
        }

        const activityRowsSource = (() => {
            if (Array.isArray(structured.stepThree.energyActivityRows) && structured.stepThree.energyActivityRows.length) {
                return structured.stepThree.energyActivityRows;
            }
            if (Array.isArray(structured.stepThree.energyExposures) && structured.stepThree.energyExposures.length) {
                return structured.stepThree.energyExposures;
            }
            return [];
        })();

        const activityRows = activityRowsSource.length
            ? activityRowsSource.map(normalizeRow)
            : [normalizeRow({})];

        structured.stepThree.energyActivityRows = activityRows;
        structured.stepThree.energyExposures = activityRows;

        // Save migrated structure back to IndexedDB
        await store.put(structured);

        setJhaData(structured);
        setLoading(false);
    }, [isNew, jha, navigate]);

    const flushSave = useCallback(async () => {
        if (!pendingRecordRef.current) {
            setSaving(false);
            return;
        }

        const recordToSave = pendingRecordRef.current;
        pendingRecordRef.current = null;

        const db = await initializeDB();
        const tx = db.transaction(JHA_STORE, 'readwrite');
        const store = tx.objectStore(JHA_STORE);
        await store.put(recordToSave);
        await tx.done;
        setSaving(false);
        window.dispatchEvent(new Event('jhaUpdated'));
    }, []);

    const scheduleSave = useCallback((record, options = {}) => {
        const { immediate = false, minStep = 3 } = options;
        const currentLast = jhaData?.meta?.lastStep || 1;
        const recordLast = record?.meta?.lastStep || currentLast;
        const nextLast = Math.max(currentLast, recordLast, minStep);

        const recordWithMeta = {
            ...record,
            meta: { ...(record.meta || {}), lastStep: nextLast }
        };

        if (isSubmitted || isReadOnly) {
            setJhaData(recordWithMeta);
            return;
        }
        setJhaData(recordWithMeta);
        pendingRecordRef.current = recordWithMeta;
        setSaving(true);

        if (immediate) {
            flushSave();
            return;
        }

        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        saveTimeoutRef.current = setTimeout(() => {
            flushSave();
        }, 400);
    }, [flushSave, isSubmitted, isReadOnly, jhaData]);

    const updateRows = rows => {
        if (!jhaData) return;
        const normalizedRows = Array.isArray(rows) && rows.length ? rows.map(normalizeRow) : [normalizeRow({})];
        const uniqueActivities = normalizedRows
            .map(row => row.workActivity)
            .filter(Boolean);

        const updated = {
            ...jhaData,
            stepThree: {
                ...jhaData.stepThree,
                energyExposures: normalizedRows,
                energyActivityRows: normalizedRows,
                workActivities: uniqueActivities,
                // Preserve other fields like customWorkActivities, tasks, etc.
                customWorkActivities: jhaData.stepThree.customWorkActivities || [],
                tasks: jhaData.stepThree.tasks || [],
                customTasks: jhaData.stepThree.customTasks || [],
                weatherConditions: jhaData.stepThree.weatherConditions || []
            }
        };
        if (updated.status === STATUS_NEW) {
            updated.status = STATUS_PROCESS;
        }
        scheduleSave(updated, { minStep: 3 });
    };

    const handleWorkActivityChange = (rowId, newValue) => {
        if (!jhaData || isReadOnly) return;
        const rows = (jhaData.stepThree.energyExposures || []).map(row =>
            row.id === rowId ? { ...row, workActivity: newValue || '' } : row
        );
        updateRows(rows);
    };

    const handleEnergySelectionsChange = (rowId, selectedOptions) => {
        if (!jhaData || isReadOnly) return;

        const rows = (jhaData.stepThree.energyExposures || []).map(row => {
            if (row.id !== rowId) return row;

            const currentSelections = Array.isArray(row.energySelections) ? row.energySelections : [];
            // Каждая сохранённая строка отдаётся максимум одной опции: иначе две
            // копии одной энергии (справочник был с дублями) забрали бы хазарды
            // друг у друга.
            const claimed = new Set();
            const mappedSelections = (selectedOptions || []).map((option, index) => {
                if (!option) return null;
                const energyId = option.id || option.ID || option.value;
                const energyKey = option._key || energyId;
                const optionName = normalizeName(option.value || option.name);
                // Сопоставление по имени нужно для JHA, сохранённых до схлопывания
                // дублей: там energyKey указывает на копию, которой больше нет в
                // списке. Без него выбранные хазарды молча исчезали.
                const existingIndex = currentSelections.findIndex((sel, selIndex) => {
                    if (claimed.has(selIndex)) return false;
                    if (sel.energyKey && sel.energyKey === energyKey) return true;
                    if (sel.energyId && sel.energyId === energyId) return true;
                    return Boolean(optionName) && normalizeName(sel.energyName) === optionName;
                });
                const existing = existingIndex === -1 ? undefined : currentSelections[existingIndex];
                if (existingIndex !== -1) {
                    claimed.add(existingIndex);
                }
                const hazardOptions = Array.isArray(option.hazards) ? option.hazards : [];
                const hazardLookup = new Map(hazardOptions.map(hazard => [getHazardKey(hazard), hazard]));
                const sanitizedHazards = (existing?.hazards || [])
                    .map(hazard => hazardLookup.get(getHazardKey(hazard)) || hazard)
                    .filter(hazard => Boolean(getHazardKey(hazard)));
                return {
                    id: existing?.id || `sel-${rowId}-${energyId}-${index}`,
                    energyKey,
                    energyId,
                    energyName: option.value || option.name || existing?.energyName || '',
                    hazards: sanitizedHazards
                };
            }).filter(Boolean);

            const primary = mappedSelections[0] || {};

            return {
                ...row,
                energySelections: mappedSelections,
                energyId: primary.energyId || null,
                energyName: primary.energyName || '',
                hazards: primary.hazards || []
            };
        });

        updateRows(rows);
    };

    const handleHazardsChange = (rowId, selectionId, hazards) => {
        if (!jhaData || isReadOnly) return;
        if (!selectionId) return;

        const rows = (jhaData.stepThree.energyExposures || []).map(row => {
            if (row.id !== rowId) return row;

            const updatedSelections = (row.energySelections || []).map(selection => {
                if (selection.id !== selectionId) return selection;
                return {
                    ...selection,
                    hazards: Array.isArray(hazards) ? hazards.map(item => ({ ...item })) : []
                };
            });

            const primary = updatedSelections[0] || {};

            return {
                ...row,
                energySelections: updatedSelections,
                energyId: primary.energyId || null,
                energyName: primary.energyName || '',
                hazards: primary.hazards || []
            };
        });

        updateRows(rows);
    };

    const handleHazardActionsConfirm = (hazard, selectedActions) => {
        if (!pendingSelection || !jhaData) return;
        const { rowId, selectionId, isEdit, editIndex } = pendingSelection;

        // Create hazard with selected actions
        const hazardWithActions = {
            ...hazard,
            preventHazards: selectedActions
        };
        // Remove edit-only properties
        delete hazardWithActions._editIndex;
        delete hazardWithActions.isNewCustom;

        // Get current hazards for this selection
        const currentRow = (jhaData.stepThree.energyExposures || []).find(r => r.id === rowId);
        const currentSelection = (currentRow?.energySelections || []).find(s => s.id === selectionId);
        const currentHazards = currentSelection?.hazards || [];

        let newHazards;
        if (isEdit && editIndex !== undefined) {
            // Edit mode: replace hazard at editIndex
            newHazards = [...currentHazards];
            newHazards[editIndex] = hazardWithActions;
        } else {
            // Add mode: append new hazard
            newHazards = [...currentHazards, hazardWithActions];
        }

        handleHazardsChange(rowId, selectionId, newHazards);

        // Close modal and clear pending state
        setHazardModalOpen(false);
        setPendingHazard(null);
        setPendingSelection(null);
    };

    const handleHazardModalClose = () => {
        setHazardModalOpen(false);
        setPendingHazard(null);
        setPendingSelection(null);
    };

    const handleAddRow = () => {
        if (!jhaData || isSubmitted || isReadOnly) return;
        const rows = [...(jhaData.stepThree.energyExposures || []), normalizeRow({})];
        updateRows(rows);
    };

    const handleRemoveRow = rowId => {
        if (!jhaData || isSubmitted || isReadOnly) return;
        const rows = (jhaData.stepThree.energyExposures || []).filter(row => row.id !== rowId);
        updateRows(rows.length ? rows : [normalizeRow({})]);
    };

    // Handle unlock editing when crew has already signed
    const handleUnlockEditing = async () => {
        const signedCount = crewAcknowledgments.filter(ack => ack.signature).length;

        const result = await Swal.fire({
            title: 'Unlock Editing?',
            html: `
                <div style="text-align: left; font-size: 14px;">
                    <p style="margin-bottom: 12px;">
                        <strong>${signedCount} crew member${signedCount > 1 ? 's have' : ' has'}</strong> already signed this JHA.
                    </p>
                    <p style="margin-bottom: 12px; color: #d32f2f;">
                        If you edit the activities or hazards, <strong>all crew signatures will be cleared</strong> and they will need to sign again.
                    </p>
                    <p style="color: #666;">
                        Are you sure you want to proceed?
                    </p>
                </div>
            `,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d32f2f',
            cancelButtonColor: '#757575',
            confirmButtonText: 'Yes, unlock and clear signatures',
            cancelButtonText: 'Cancel',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            // Clear all crew acknowledgments completely
            const updated = {
                ...jhaData,
                stepFour: {
                    ...jhaData.stepFour,
                    employeeAcknowledgments: []
                }
            };

            // Save directly to IndexedDB (bypass isReadOnly check)
            try {
                const db = await initializeDB();
                const tx = db.transaction(JHA_STORE, 'readwrite');
                const store = tx.objectStore(JHA_STORE);
                await store.put(updated);
                await tx.done;

                setJhaData(updated);
                setLockLifted(true);

                // Notify other components about the update
                window.dispatchEvent(new Event('jhaUpdated'));

                Swal.fire({
                    icon: 'success',
                    title: 'Unlocked',
                    text: 'Editing unlocked. All crew acknowledgments have been cleared.',
                    timer: 2500,
                    showConfirmButton: false
                });
            } catch (error) {
                console.error('Failed to save:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to unlock editing. Please try again.'
                });
            }
        }
    };

    const handleBack = async () => {
        pendingRecordRef.current = jhaData;
        await flushSave();
        navigate('/jha/step-1', { state: { jha: jhaData, isNew: false, viewMode } });
    };

    const handleNext = async () => {
        const updated = {
            ...jhaData,
            meta: {
                ...(jhaData?.meta || {}),
                lastStep: Math.max(jhaData?.meta?.lastStep || 1, 3)
            }
        };
        setJhaData(updated);
        pendingRecordRef.current = updated;
        await flushSave();
        // Documents step is now position 3 (steps swapped)
        navigate('/jha/step-3', { state: { jha: updated, isNew: false, viewMode } });
    };

    if (loading || !jhaData) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    const rows = jhaData.stepThree.energyExposures || [];
    const hasSelection = rows.some(row =>
        Array.isArray(row.energySelections) && row.energySelections.some(sel => sel.energyId)
    );
    const allSelectionsHaveHazards = rows.every(row =>
        (row.energySelections || []).every(sel => !sel.energyId || (sel.hazards && sel.hazards.length))
    );
    const allActiveRowsHaveActivity = rows.every(row => {
        const hasEnergy = Array.isArray(row.energySelections) && row.energySelections.some(sel => sel.energyId);
        if (!hasEnergy) {
            return true;
        }
        return Boolean(row.workActivity && row.workActivity.trim());
    });
    // Activities valid = ready to advance to Documents. Signature + Send Initial JHA
    // moved to the Documents step (steps swapped), so signature is no longer gated here.
    const activitiesValid = Boolean(hasSelection && allSelectionsHaveHazards && allActiveRowsHaveActivity);
    const showSavingIndicator = saving && !isReadOnly;

    return (
        <Box
            sx={{
                height: 'calc(100vh - 64px)',
                backgroundColor: '#f5f5f5',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden'
            }}
        >
            <JhaStepHeader
                currentStep={2}
                jhaData={jhaData}
                fromViewer={fromViewer}
            />

            <Box sx={{ flex: 1, overflow: 'auto', display: 'flex', justifyContent: 'center', minHeight: 0 }}>
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: 1200,
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        px: { xs: 0.5, md: 1 },
                        py: { xs: 0.5, md: 1 }
                    }}
                >
                    <Stack spacing={1} sx={{ flex: 1 }}>
                        {/* ─── Generic triggered-documents banner ─────────────────
                             Live — обновляется при выборе WA/Energy/Hazard.
                             Показываем какие документы стали Required по триггерам
                             и почему. Кнопка ведёт на шаг Documents (step-3) для прикрепления.
                        */}
                        {triggeredMissing.length > 0 && !isReadOnly && (
                            <Alert
                                severity="warning"
                                icon={<WarningIcon />}
                                action={
                                    <Button
                                        color="warning"
                                        variant="contained"
                                        size="small"
                                        startIcon={<OpenInNewIcon />}
                                        onClick={() => {
                                            navigate('/jha/step-3', { state: { jha: jhaData, isNew: false, viewMode } });
                                        }}
                                    >
                                        Go to Documents
                                    </Button>
                                }
                                sx={{ '& .MuiAlert-message': { flex: 1 } }}
                            >
                                <strong>
                                    {triggeredMissing.length === 1
                                        ? 'Required document missing'
                                        : `${triggeredMissing.length} required documents missing`}
                                </strong>
                                <Box component="ul" sx={{ pl: 2.5, mt: 0.5, mb: 0 }}>
                                    {triggeredMissing.map((m) => (
                                        <li key={m.document_key}>
                                            <strong>{m.document_name}</strong>
                                            <Box component="span" sx={{ color: 'text.secondary', fontSize: 12, display: 'block' }}>
                                                {m.reason}
                                            </Box>
                                        </li>
                                    ))}
                                </Box>
                            </Alert>
                        )}

                        {/* ─── Fall Arrest Rescue Plan banner ─────────────────────
                             Appears when one or more selected work activities trigger FARP.
                             - WARNING (orange): no submitted FARP yet for this job + date
                             - SUCCESS (green): FARP already submitted, with link to view it
                        */}
                        {farpRequired && (
                            <Alert
                                severity={farpSatisfied ? 'success' : 'warning'}
                                icon={farpSatisfied ? <CheckCircleIcon /> : <HealthAndSafetyIcon />}
                                action={
                                    farpExistingPlan ? (
                                        <Stack direction="row" spacing={1}>
                                            <Button
                                                color="inherit"
                                                size="small"
                                                startIcon={<PictureAsPdfIcon />}
                                                onClick={viewExistingFarpPdf}
                                            >
                                                View PDF
                                            </Button>
                                        </Stack>
                                    ) : (!farpSatisfied && (
                                        <Button
                                            color="warning"
                                            variant="contained"
                                            size="small"
                                            startIcon={<OpenInNewIcon />}
                                            onClick={openFarpForm}
                                            disabled={!farpJobCode}
                                        >
                                            Open Fall Arrest Rescue Plan
                                        </Button>
                                    ))
                                }
                                sx={{ '& .MuiAlert-message': { flex: 1 } }}
                            >
                                {farpExistingPlan ? (
                                    <>
                                        <strong>Fall Arrest Rescue Plan submitted</strong> for this job (
                                        {farpExistingPlan.planDate}
                                        {farpExistingPlan.submitterName ? ` · by ${farpExistingPlan.submitterName}` : ''}
                                        ). Triggered by: <em>{triggeredActivities.length ? triggeredActivities.join(', ') : farpReason}</em>.
                                    </>
                                ) : farpSatisfied ? (
                                    <>
                                        <strong>Fall Arrest Rescue Plan attached</strong> — requirement met. Triggered by:{' '}
                                        <em>{triggeredActivities.length ? triggeredActivities.join(', ') : farpReason}</em>.
                                    </>
                                ) : (
                                    <>
                                        <strong>Fall Arrest Rescue Plan required</strong> — triggered by{' '}
                                        <em>{triggeredActivities.length ? triggeredActivities.join(', ') : farpReason}</em>.
                                        {' '}File the rescue plan before work begins.
                                        {!farpJobCode && (
                                            <span style={{ display: 'block', marginTop: 4, fontStyle: 'italic' }}>
                                                Select a Job in Step 1 first to open the form pre-filled.
                                            </span>
                                        )}
                                        {farpCheckLoading && (
                                            <span style={{ display: 'inline-block', marginLeft: 8, fontSize: 12 }}>
                                                Checking…
                                            </span>
                                        )}
                                        {/* Offline disclaimer: backend check недоступен → возможно plan уже подан, но мы об этом не знаем */}
                                        {!farpCheckLoading && typeof navigator !== 'undefined' && !navigator.onLine && (
                                            <span style={{ display: 'block', marginTop: 4, fontSize: 12, fontStyle: 'italic', opacity: 0.8 }}>
                                                You appear to be offline — existing-plan status could not be verified with the server.
                                            </span>
                                        )}
                                    </>
                                )}
                            </Alert>
                        )}

                        {/* Lock overlay when crew has signed */}
                        <Box sx={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column' }}>
                            {isEditingLocked && (
                                <Box
                                    onClick={handleUnlockEditing}
                                    sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor: 'rgba(0, 0, 0, 0.03)',
                                        backdropFilter: 'blur(1px)',
                                        zIndex: 10,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        borderRadius: 1,
                                        border: '2px dashed #ff9800',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            backgroundColor: 'rgba(255, 152, 0, 0.08)',
                                            borderColor: '#f57c00'
                                        }
                                    }}
                                >
                                    <Stack
                                        alignItems="center"
                                        spacing={1}
                                        sx={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                            px: 3,
                                            py: 2,
                                            borderRadius: 2,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                borderRadius: '50%',
                                                backgroundColor: '#fff3e0',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <LockIcon sx={{ fontSize: 28, color: '#ff9800' }} />
                                        </Box>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#e65100' }}>
                                            Editing Locked
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 280 }}>
                                            Crew members have already signed this JHA.
                                            <br />
                                            <strong>Tap here to unlock</strong> (signatures will be cleared)
                                        </Typography>
                                    </Stack>
                                </Box>
                            )}

                            <TableContainer component={Paper} sx={{ border: '1px solid #c3cbd4', flex: 1, opacity: isEditingLocked ? 0.6 : 1 }}>
                                <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600, width: '45%', padding: '6px 8px' }}>
                                            Work Activity &amp; Energy
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600, width: '55%', padding: '6px 8px' }}>
                                            Hazards by Energy
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {rows.map(row => {
                                        const energySelections = Array.isArray(row.energySelections) ? row.energySelections : [];
                                        const selectedEnergyOptions = energySelections
                                            .map(selection => {
                                                const known = resolveEnergyOption(selection);
                                                if (known) {
                                                    return known;
                                                }
                                                if (selection.energyId || selection.energyName) {
                                                    const fallbackKey = selection.energyKey || selection.id || selection.energyId || selection.energyName;
                                                    return {
                                                        _key: fallbackKey,
                                                        id: selection.energyId || fallbackKey,
                                                        value: selection.energyName || selection.energyId || 'Energy'
                                                    };
                                                }
                                                return null;
                                            })
                                            .filter(Boolean);

                                        return (
                                            <TableRow
                                                key={row.id}
                                                sx={{
                                                    '& > td': {
                                                        borderBottom: '1px solid #b7c0cc'
                                                    }
                                                }}
                                            >
                                                <TableCell sx={{ padding: '6px 8px', verticalAlign: 'top' }}>
                                                    <Stack spacing={0.75}>
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.25 }}>
                                                                Work Activity
                                                            </Typography>
                                                            <Stack direction="row" spacing={0.5} alignItems="center">
                                                                <Tooltip title={row.workActivity || ''} placement="top">
                                                                    <Autocomplete
                                                                        options={[
                                                                            { value: '__ADD_CUSTOM__', label: '+ Add custom activity', isCustomOption: true },
                                                                            ...workActivitiesList
                                                                        ]}
                                                                        getOptionLabel={option => {
                                                                            if (option.isCustomOption) return option.label;
                                                                            return option.value || '';
                                                                        }}
                                                                        value={(() => {
                                                                            // Ищем в списке
                                                                            const found = workActivitiesList.find(item => item.value === row.workActivity);
                                                                            if (found) return found;
                                                                            // Если не нашли, но есть значение - показываем кастомное
                                                                            if (row.workActivity) return { value: row.workActivity, isCustomValue: true };
                                                                            return null;
                                                                        })()}
                                                                        onChange={(_, newValue) => {
                                                                            if (newValue?.isCustomOption) {
                                                                                // Показываем prompt для ввода кастомного значения
                                                                                const customActivity = window.prompt('Enter custom work activity:');
                                                                                if (customActivity && customActivity.trim()) {
                                                                                    handleWorkActivityChange(row.id, customActivity.trim());
                                                                                }
                                                                            } else {
                                                                                handleWorkActivityChange(row.id, newValue?.value || '');
                                                                            }
                                                                        }}
                                                                        renderOption={(props, option) => {
                                                                            const { key, ...otherProps } = props;
                                                                            return (
                                                                                <li key={key} {...otherProps} style={{
                                                                                    fontWeight: option.isCustomOption ? 600 : 400,
                                                                                    color: option.isCustomOption ? '#1976d2' : 'inherit',
                                                                                    borderBottom: option.isCustomOption ? '1px solid #e0e0e0' : 'none',
                                                                                    marginBottom: option.isCustomOption ? '4px' : 0
                                                                                }}>
                                                                                    {option.isCustomOption ? option.label : option.value}
                                                                                </li>
                                                                            );
                                                                        }}
                                                                        renderInput={params => (
                                                                            <TextField
                                                                                {...params}
                                                                                placeholder="Select activity"
                                                                                size="small"
                                                                                fullWidth
                                                                                sx={{
                                                                                    '& .MuiInputBase-input': {
                                                                                        fontSize: '12px'
                                                                                    }
                                                                                }}
                                                                            />
                                                                        )}
                                                                        disabled={isReadOnly}
                                                                        fullWidth
                                                                        sx={{ flex: 1 }}
                                                                    />
                                                                </Tooltip>
                                                                {!isReadOnly && rows.length > 1 && (
                                                                    <IconButton
                                                                        color="error"
                                                                        size="small"
                                                                        onClick={() => handleRemoveRow(row.id)}
                                                                        sx={{ p: 0.25 }}
                                                                    >
                                                                        <DeleteIcon sx={{ fontSize: 16 }} />
                                                                    </IconButton>
                                                                )}
                                                            </Stack>
                                                        </Box>
                                                        <Box>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 0.25 }}>
                                                                Energy Sources
                                                            </Typography>
                                                            <Box>
                                                                <Autocomplete
                                                                    multiple
                                                                    options={energyList}
                                                                    value={selectedEnergyOptions}
                                                                    getOptionLabel={option => option.value || ''}
                                                                    isOptionEqualToValue={(option, value) => option._key === value._key}
                                                                    onChange={(_, newOptions) => handleEnergySelectionsChange(row.id, newOptions)}
                                                                    filterSelectedOptions
                                                                    disableCloseOnSelect
                                                                    renderTags={(value, getTagProps) =>
                                                                        value.map((option, index) => {
                                                                            const { key: tagKey, ...chipProps } = getTagProps({ index });
                                                                            return (
                                                                                <Chip
                                                                                    key={tagKey ?? option._key ?? index}
                                                                                    label={option.value}
                                                                                    size="small"
                                                                                    sx={{ fontSize: '11px', height: '20px' }}
                                                                                    {...chipProps}
                                                                                />
                                                                            );
                                                                        })
                                                                    }
                                                                    renderOption={(props, option) => (
                                                                        <li {...props} key={option._key}>
                                                                            <Box>
                                                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                                    {option.value || option.name || 'Unnamed energy'}
                                                                                </Typography>
                                                                                {option.description && (
                                                                                    <Typography
                                                                                        variant="caption"
                                                                                        sx={{
                                                                                            color: 'text.secondary',
                                                                                            display: 'block',
                                                                                            fontStyle: 'italic',
                                                                                            mt: 0.25
                                                                                        }}
                                                                                    >
                                                                                        {option.description}
                                                                                    </Typography>
                                                                                )}
                                                                            </Box>
                                                                        </li>
                                                                    )}
                                                                    renderInput={params => (
                                                                        <TextField
                                                                            {...params}
                                                                            placeholder="Select energy"
                                                                            size="small"
                                                                            fullWidth
                                                                            sx={{
                                                                                '& .MuiInputBase-input': {
                                                                                    fontSize: '13px'
                                                                                }
                                                                            }}
                                                                        />
                                                                    )}
                                                                    disabled={isReadOnly}
                                                                    size="small"
                                                                    fullWidth
                                                                />
                                                            </Box>
                                                        </Box>
                                                    </Stack>
                                                </TableCell>

                                                <TableCell sx={{ padding: '6px 8px', verticalAlign: 'top' }}>
                                                    {energySelections.length > 0 ? (
                                                        <Stack spacing={1}>
                                                            {energySelections.map(selection => {
                                                                const energyOption = resolveEnergyOption(selection);

                                                                const hazardOptions = (() => {
                                                                    const baseOptions = Array.isArray(energyOption?.hazards)
                                                                        ? energyOption.hazards
                                                                        : [];
                                                                    const extras = Array.isArray(selection.hazards)
                                                                        ? selection.hazards
                                                                        : [];
                                                                    if (!extras.length) {
                                                                        return [...baseOptions].sort(byName);
                                                                    }
                                                                    const map = new Map(
                                                                        baseOptions.map(hazard => [getHazardKey(hazard), hazard])
                                                                    );
                                                                    extras.forEach(hazard => {
                                                                        const key = getHazardKey(hazard);
                                                                        if (key && !map.has(key)) {
                                                                            map.set(key, hazard);
                                                                        }
                                                                    });
                                                                    // кастомные хазарды тоже встают по алфавиту
                                                                    return Array.from(map.values()).sort(byName);
                                                                })();

                                                                const energyLabel =
                                                                    selection.energyName ||
                                                                    energyOption?.value ||
                                                                    energyOption?.name ||
                                                                    'Energy Source';

                                                                const selectedHazards = Array.isArray(selection.hazards)
                                                                    ? selection.hazards
                                                                    : [];

                                                                return (
                                                                    <Box
                                                                        key={selection.id}
                                                                        sx={{
                                                                            border: '1px solid #e0e0e0',
                                                                            borderRadius: 1,
                                                                            backgroundColor: '#fafafa',
                                                                            p: 1
                                                                        }}
                                                                    >
                                                                        <Stack spacing={0.5}>
                                                                            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                                                                {energyLabel}
                                                                            </Typography>
                                                                            <Autocomplete
                                                                                multiple
                                                                                options={[
                                                                                        { value: '__ADD_CUSTOM__', label: '+ Add custom hazard', isAddCustomOption: true },
                                                                                        ...hazardOptions
                                                                                    ]}
                                                                                    value={selectedHazards}
                                                                                    onChange={(_, newValue, reason, details) => {
                                                                                        if (reason === 'selectOption' && details?.option) {
                                                                                            if (details.option.isAddCustomOption) {
                                                                                                // Open modal for entering custom hazard with dictation
                                                                                                const customHazard = {
                                                                                                    id: `new-custom-${Date.now()}`,
                                                                                                    preventHazards: [],
                                                                                                    isNewCustom: true
                                                                                                };
                                                                                                setPendingHazard(customHazard);
                                                                                                setPendingSelection({
                                                                                                    rowId: row.id,
                                                                                                    selectionId: selection.id,
                                                                                                    workActivity: row.workActivity || '',
                                                                                                    energyName: energyLabel
                                                                                                });
                                                                                                setHazardModalOpen(true);
                                                                                            } else {
                                                                                                // Open modal for selecting actions
                                                                                                setPendingHazard(details.option);
                                                                                                setPendingSelection({
                                                                                                    rowId: row.id,
                                                                                                    selectionId: selection.id,
                                                                                                    workActivity: row.workActivity || '',
                                                                                                    energyName: energyLabel
                                                                                                });
                                                                                                setHazardModalOpen(true);
                                                                                            }
                                                                                        } else if (reason === 'removeOption') {
                                                                                            // Removal works as before
                                                                                            handleHazardsChange(row.id, selection.id, newValue);
                                                                                        } else if (reason === 'clear') {
                                                                                            // Clear all hazards
                                                                                            handleHazardsChange(row.id, selection.id, []);
                                                                                        }
                                                                                    }}
                                                                                    getOptionLabel={option => {
                                                                                        if (option.isAddCustomOption) return option.label;
                                                                                        const fullText = option.name || option.value || '';
                                                                                        const colonIndex = fullText.indexOf(':');
                                                                                        return colonIndex > 0 ? fullText.substring(0, colonIndex).trim() : fullText;
                                                                                    }}
                                                                                    isOptionEqualToValue={(option, value) => {
                                                                                        if (option.isAddCustomOption) return false;
                                                                                        return getHazardKey(option) === getHazardKey(value);
                                                                                    }}
                                                                                    filterOptions={(options, state) => {
                                                                                        // Always keep "+ Add custom hazard" option, filter others
                                                                                        const addCustomOption = options.find(o => o.isAddCustomOption);
                                                                                        const otherOptions = options.filter(o => !o.isAddCustomOption);

                                                                                        // Filter by input and exclude already selected
                                                                                        const selectedKeys = new Set(selectedHazards.map(h => getHazardKey(h)));
                                                                                        const filtered = otherOptions.filter(option => {
                                                                                            const label = option.name || option.value || '';
                                                                                            const matchesInput = label.toLowerCase().includes(state.inputValue.toLowerCase());
                                                                                            const notSelected = !selectedKeys.has(getHazardKey(option));
                                                                                            return matchesInput && notSelected;
                                                                                        });

                                                                                        return addCustomOption ? [addCustomOption, ...filtered] : filtered;
                                                                                    }}
                                                                                    renderTags={() => null}
                                                                                    renderOption={(props, option) => {
                                                                                        const { key, ...otherProps } = props;

                                                                                        // "+ Add custom hazard" option
                                                                                        if (option.isAddCustomOption) {
                                                                                            return (
                                                                                                <li
                                                                                                    key={key}
                                                                                                    {...otherProps}
                                                                                                    style={{
                                                                                                        fontWeight: 600,
                                                                                                        color: '#1976d2',
                                                                                                        borderBottom: '1px solid #e0e0e0',
                                                                                                        marginBottom: '4px',
                                                                                                        padding: '8px 16px'
                                                                                                    }}
                                                                                                >
                                                                                                    {option.label}
                                                                                                </li>
                                                                                            );
                                                                                        }

                                                                                        const fullText = option.name || option.value || 'Hazard';
                                                                                        const colonIndex = fullText.indexOf(':');
                                                                                        const title = colonIndex > 0 ? fullText.substring(0, colonIndex).trim() : fullText;
                                                                                        const description = colonIndex > 0 ? fullText.substring(colonIndex + 1).trim() : null;

                                                                                        return (
                                                                                            <li
                                                                                                key={key}
                                                                                                {...otherProps}
                                                                                                style={{
                                                                                                    borderBottom: '1px solid #e0e0e0',
                                                                                                    padding: '8px 16px'
                                                                                                }}
                                                                                            >
                                                                                                <Box>
                                                                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                                                                        {title}
                                                                                                    </Typography>
                                                                                                    {description && (
                                                                                                        <Typography
                                                                                                            variant="caption"
                                                                                                            sx={{
                                                                                                                color: 'text.secondary',
                                                                                                                display: 'block',
                                                                                                                fontStyle: 'italic',
                                                                                                                mt: 0.25
                                                                                                            }}
                                                                                                        >
                                                                                                            {description}
                                                                                                        </Typography>
                                                                                                    )}
                                                                                                </Box>
                                                                                            </li>
                                                                                        );
                                                                                    }}
                                                                                    renderInput={params => (
                                                                                        <TextField
                                                                                            {...params}
                                                                                            placeholder="Select hazards"
                                                                                            size="small"
                                                                                            InputProps={{
                                                                                                ...params.InputProps,
                                                                                                startAdornment: selectedHazards.length > 0 ? (
                                                                                                    <Chip
                                                                                                        label={`${selectedHazards.length} hazard${selectedHazards.length > 1 ? 's' : ''} selected`}
                                                                                                        size="small"
                                                                                                        sx={{
                                                                                                            fontSize: '11px',
                                                                                                            height: '20px',
                                                                                                            ml: 0.5
                                                                                                        }}
                                                                                                    />
                                                                                                ) : null
                                                                                            }}
                                                                                            sx={{
                                                                                                '& .MuiInputBase-input': {
                                                                                                    fontSize: '13px'
                                                                                                }
                                                                                            }}
                                                                                        />
                                                                                    )}
                                                                                    disableCloseOnSelect
                                                                                    size="small"
                                                                                    disabled={isReadOnly}
                                                                                />
                                                                                {/* Selected hazards list */}
                                                                                {selectedHazards.length > 0 && (
                                                                                    <Box sx={{ mt: 1 }}>
                                                                                        {selectedHazards.map((hazard, idx) => {
                                                                                            const hazardLabel = (() => {
                                                                                                const fullText = hazard.name || hazard.value || 'Hazard';
                                                                                                const colonIndex = fullText.indexOf(':');
                                                                                                return colonIndex > 0 ? fullText.substring(0, colonIndex).trim() : fullText;
                                                                                            })();
                                                                                            const actions = hazard.preventHazards || [];
                                                                                            const actionsText = actions.length > 0
                                                                                                ? actions.map(a => typeof a === 'string' ? a : (a.value || a.name || '')).filter(Boolean).join(', ')
                                                                                                : 'No actions';

                                                                                            return (
                                                                                                <Box
                                                                                                    key={hazard.id || hazard._key || idx}
                                                                                                    sx={{
                                                                                                        display: 'flex',
                                                                                                        alignItems: 'flex-start',
                                                                                                        gap: 0.5,
                                                                                                        p: 0.75,
                                                                                                        mb: 0.5,
                                                                                                        bgcolor: hazard.isCustom ? 'primary.50' : 'grey.50',
                                                                                                        borderRadius: 1,
                                                                                                        border: '1px solid',
                                                                                                        borderColor: hazard.isCustom ? 'primary.200' : 'grey.300'
                                                                                                    }}
                                                                                                >
                                                                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                                                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '12px' }}>
                                                                                                            {hazardLabel}
                                                                                                            {hazard.isCustom && (
                                                                                                                <Typography
                                                                                                                    component="span"
                                                                                                                    sx={{
                                                                                                                        ml: 0.5,
                                                                                                                        bgcolor: 'primary.main',
                                                                                                                        color: 'white',
                                                                                                                        px: 0.5,
                                                                                                                        py: 0.125,
                                                                                                                        borderRadius: 0.5,
                                                                                                                        fontSize: '9px',
                                                                                                                        fontWeight: 600
                                                                                                                    }}
                                                                                                                >
                                                                                                                    CUSTOM
                                                                                                                </Typography>
                                                                                                            )}
                                                                                                        </Typography>
                                                                                                        <Typography
                                                                                                            variant="caption"
                                                                                                            color="text.secondary"
                                                                                                            sx={{
                                                                                                                display: 'block',
                                                                                                                fontSize: '10px',
                                                                                                                overflow: 'hidden',
                                                                                                                textOverflow: 'ellipsis',
                                                                                                                whiteSpace: 'nowrap'
                                                                                                            }}
                                                                                                        >
                                                                                                            {actionsText}
                                                                                                        </Typography>
                                                                                                    </Box>
                                                                                                    {!isReadOnly && (
                                                                                                        <Stack direction="row" spacing={0.25}>
                                                                                                            <IconButton
                                                                                                                size="small"
                                                                                                                onClick={() => {
                                                                                                                    // Find original hazard to get all available actions
                                                                                                                    const originalHazard = hazardOptions.find(h =>
                                                                                                                        (h.id && h.id === hazard.id) ||
                                                                                                                        (h._key && h._key === hazard._key) ||
                                                                                                                        (h.value === hazard.value && h.name === hazard.name)
                                                                                                                    );
                                                                                                                    const allAvailableActions = originalHazard?.preventHazards || [];

                                                                                                                    // Edit: reopen modal with this hazard
                                                                                                                    setPendingHazard({
                                                                                                                        ...hazard,
                                                                                                                        _editIndex: idx,
                                                                                                                        _allPreventHazards: allAvailableActions,
                                                                                                                        _selectedPreventHazards: hazard.preventHazards || []
                                                                                                                    });
                                                                                                                    setPendingSelection({
                                                                                                                        rowId: row.id,
                                                                                                                        selectionId: selection.id,
                                                                                                                        workActivity: row.workActivity || '',
                                                                                                                        energyName: energyLabel,
                                                                                                                        isEdit: true,
                                                                                                                        editIndex: idx
                                                                                                                    });
                                                                                                                    setHazardModalOpen(true);
                                                                                                                }}
                                                                                                                sx={{ p: 0.25 }}
                                                                                                                title="Edit"
                                                                                                            >
                                                                                                                <EditIcon sx={{ fontSize: 14 }} />
                                                                                                            </IconButton>
                                                                                                            <IconButton
                                                                                                                size="small"
                                                                                                                onClick={() => {
                                                                                                                    // Remove this hazard
                                                                                                                    const newHazards = selectedHazards.filter((_, i) => i !== idx);
                                                                                                                    handleHazardsChange(row.id, selection.id, newHazards);
                                                                                                                }}
                                                                                                                sx={{ p: 0.25 }}
                                                                                                                title="Remove"
                                                                                                            >
                                                                                                                <DeleteIcon sx={{ fontSize: 14 }} />
                                                                                                            </IconButton>
                                                                                                        </Stack>
                                                                                                    )}
                                                                                                </Box>
                                                                                            );
                                                                                        })}
                                                                                    </Box>
                                                                                )}
                                                                        </Stack>
                                                                    </Box>
                                                                );
                                                            })}
                                                        </Stack>
                                                    ) : (
                                                        <Typography variant="caption" color="text.secondary">
                                                            Select energy sources to see related hazards
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        </Box>

                        {!isSubmitted && (
                            <Box display="flex" justifyContent="flex-start">
                                <Button
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddRow}
                                    size="small"
                                    disabled={isEditingLocked}
                                >
                                    Add Work Activity
                                </Button>
                                {isEditingLocked && (
                                    <Chip
                                        icon={<LockIcon sx={{ fontSize: 14 }} />}
                                        label="Unlock to edit"
                                        size="small"
                                        color="warning"
                                        variant="outlined"
                                        onClick={handleUnlockEditing}
                                        sx={{ ml: 1, cursor: 'pointer' }}
                                    />
                                )}
                            </Box>
                        )}

                    </Stack>
                </Box>
            </Box>

            <Box
                sx={{
                    position: 'sticky',
                    bottom: 0,
                    backgroundColor: '#ffffff',
                    borderTop: '1px solid #e0e0e0',
                    zIndex: 5
                }}
            >
                <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ px: 0.75, py: 0.6, flexWrap: 'nowrap', columnGap: 1.5 }}
                >
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ flexWrap: 'nowrap', minWidth: 0 }}
                    >
                        <Button
                            variant="outlined"
                            startIcon={<ArrowBackIcon />}
                            onClick={handleBack}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            Back to General
                        </Button>
                        <Box sx={{ minHeight: 20, display: 'flex', alignItems: 'center', minWidth: 72 }}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    visibility: showSavingIndicator ? 'visible' : 'hidden',
                                    opacity: showSavingIndicator ? 1 : 0,
                                    transition: 'opacity 0.2s ease'
                                }}
                            >
                                Saving…
                            </Typography>
                        </Box>
                    </Stack>
                    <Stack
                        direction="row"
                        spacing={1.25}
                        alignItems="center"
                        sx={{ flexWrap: 'nowrap', minWidth: 0, '& > *': { flexShrink: 0 } }}
                    >
                        {!activitiesValid && (
                            <Chip
                                label="Complete activities & hazards to continue"
                                color="warning"
                                size="small"
                                sx={{ whiteSpace: 'nowrap' }}
                            />
                        )}
                        <Button
                            variant="contained"
                            endIcon={<ArrowForwardIcon />}
                            onClick={handleNext}
                            disabled={!activitiesValid}
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            Continue to Documents
                        </Button>
                    </Stack>
                </Stack>
            </Box>

            <HazardActionsModal
                open={hazardModalOpen}
                hazard={pendingHazard}
                workActivity={pendingSelection?.workActivity || ''}
                energyName={pendingSelection?.energyName || ''}
                onClose={handleHazardModalClose}
                onConfirm={handleHazardActionsConfirm}
            />
        </Box>
    );
}
